import React, { Fragment, useCallback, useEffect, useState, useRef } from 'react';
import Slider from 'js/components/common/Slider';
import { uniqueId } from 'js/utils/methods.util';

import * as hooks from '../../../../hooks';
import * as helpers from '../../../../helpers';
import { CarouselItem } from '../CarouselItem';
import { CAROUSEL_SLIDER_OPTIONS, CAROUSEL_MIN_ITEMS_PER_SLIDE } from '../../../../constants';

import style from './GridPagingSlider.scss';

export const GridPagingSlider = () => {
    const frame = useRef();
    const winnerItemRef = useRef();
    const containerRef = useRef();
    const topWinners = hooks.useTopWinners();
    const getSlides = useCallback((itemsPerSlide) => helpers.getSlides(itemsPerSlide, topWinners), [topWinners]);
    const [slides, setSlides] = useState(getSlides(CAROUSEL_MIN_ITEMS_PER_SLIDE));

    const updateSlides = useCallback(() => {
        if (!containerRef.current || !winnerItemRef.current) {
            return;
        }

        const totalGamesCount = helpers.getTotalGamesCount(containerRef.current, winnerItemRef.current);

        if (slides[0].length !== totalGamesCount) {
            setSlides(getSlides(totalGamesCount));
        }
    }, [slides, getSlides]);

    const setSlideRef = (ref) => {
        containerRef.current = ref;
        updateSlides();
    };

    const setWinnerItemRef = (ref) => {
        winnerItemRef.current = ref;
        updateSlides();
    };

    const throttledCalculatedGamePerSlide = useCallback(() => {
        cancelAnimationFrame(frame.current);
        frame.current = requestAnimationFrame(updateSlides);
    }, [updateSlides]);

    useEffect(() => {
        window.addEventListener('resize', throttledCalculatedGamePerSlide);

        return () => {
            window.removeEventListener('resize', throttledCalculatedGamePerSlide);
        };
    }, [throttledCalculatedGamePerSlide]);

    return (
        <Slider
            swipe={false}
            rotation={false}
            renderAll={false}
            showDisabledArrows={true}
            showControlsAtStart={true}
            refs={{ slide: setSlideRef }}
            options={CAROUSEL_SLIDER_OPTIONS}
            classNames={{
                ...style,
                slide: style.slide,
                leftArrow: style.leftArrow,
                rightArrow: style.rightArrow,
                sliderContainer: style.sliderContainer
            }}
            className={`${style.sliderWrapper} ${slides.length <= 1 ? style.noControls : ''}`}
        >
            {slides.map((slide, i) => (
                <Fragment key={`${slide.length}_${i}_${slides[0][0]}`}>
                    {slide.map((topWinner, index) => topWinner
                        ? (
                            <CarouselItem
                                {...topWinner}
                                key={topWinner.id}
                                className={style.topWinner}
                                {...(i === 0 && index === 0 && { setWinnerItemRef })}
                            />
                        )
                        : <div key={uniqueId(index)} className={style.topWinner} />
                    )}
                </Fragment>
            ))}
        </Slider>
    );
};
